import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApiMutation, useApiQuery } from '../../api/hooks';
import type {
  AppointmentResponse,
  CompleteAtDeskRequest,
  DeskCompletedResponse,
  DeskCompletionPreviewResponse,
} from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { ErrorState } from '../../components/states/StateViews';
import { useLocalisedName } from '../../i18n/localisedName';
import { clinicTime } from './clinicTime';

interface CompleteDialogProps {
  readonly clinicId: string;
  readonly appointment: AppointmentResponse;
  /** Query keys to refresh once the session is recorded: the board, at least. */
  readonly invalidates: readonly (readonly unknown[])[];
  readonly onClose: () => void;
}

/**
 * Finishing a session at the desk (D-12, CR-27).
 *
 * Until CR-27 the day board's Complete only changed the status, so a laser session finished at the
 * desk recorded no pulses and spent nothing from the patient's package. This form asks what the
 * doctor app asks, minus everything clinical (FR-405): which areas were treated and the pulses fired
 * on each, and the machine. The server records it, spends the package and sends the aftercare in
 * one go, credited to the appointment's doctor.
 *
 * The pulse boxes start empty, with the area's reference pulses as the placeholder. The doctor app
 * pre-fills them because the doctor is the one confirming; at the desk a pre-filled box is a number
 * nobody asked the doctor for, and the machine's counter is read from these (CR-21, FR-809).
 */
export function CompleteDialog({ clinicId, appointment, invalidates, onClose }: CompleteDialogProps) {
  const { t } = useTranslation();
  const localised = useLocalisedName();

  const form = useApiQuery<DeskCompletionPreviewResponse>(
    ['clinics', clinicId, 'appointments', appointment.id ?? '', 'completion'],
    `/clinics/${clinicId}/appointments/${appointment.id}/completion`,
  );

  const complete = useApiMutation<DeskCompletedResponse, CompleteAtDeskRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/appointments/${appointment.id}/complete`,
      options: { method: 'POST', body },
    }),
    { invalidates, onSuccess: onClose },
  );

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={t('board.complete.title')}>
      <div className="modal__panel modal__panel--narrow">
        <header className="modal__header">
          <h2>{t('board.complete.title')}</h2>
        </header>
        <AsyncSection query={form} skeletonRows={3}>
          {(data) => (
            <CompleteForm
              key={data.appointmentId}
              data={data}
              heading={t('board.complete.who', {
                patient: appointment.patientName ?? '',
                service: localised.name({ nameEn: data.serviceNameEn, nameAr: data.serviceNameAr }),
                time: clinicTime(appointment.startsAt),
              })}
              pending={complete.isPending}
              error={complete.error}
              onClose={onClose}
              onSubmit={(body) => complete.mutate(body)}
            />
          )}
        </AsyncSection>
      </div>
    </div>
  );
}

function CompleteForm({
  data,
  heading,
  pending,
  error,
  onClose,
  onSubmit,
}: {
  readonly data: DeskCompletionPreviewResponse;
  readonly heading: string;
  readonly pending: boolean;
  readonly error: unknown;
  readonly onClose: () => void;
  readonly onSubmit: (body: CompleteAtDeskRequest) => void;
}) {
  const { t } = useTranslation();
  const localised = useLocalisedName();
  const areas = data.areas ?? [];
  const machines = data.machines ?? [];
  const recordsPulses = data.recordsPulses ?? false;

  // The booked area starts ticked; the others can be ticked when more was treated in the sitting.
  const [treated, setTreated] = useState<string[]>(
    areas.filter((area) => area.booked && area.serviceAreaId).map((area) => area.serviceAreaId ?? ''),
  );
  const [pulses, setPulses] = useState<Record<string, string>>({});
  const [total, setTotal] = useState('');
  // One machine is preselected rather than asked about, as the doctor app does.
  const usable = machines.filter((machine) => machine.available);
  const [machineId, setMachineId] = useState(usable.length === 1 ? (usable[0]?.machineId ?? '') : '');

  const fired = (id: string) => Number(pulses[id] ?? '');
  const withAreas = recordsPulses && areas.length > 0;
  const valid = !recordsPulses
    ? true
    : withAreas
      ? treated.length > 0 && treated.every((id) => fired(id) > 0)
      : Number(total) > 0;

  // What the session will be spent from, in the words the desk says to the patient.
  const balance = data.balance ?? 0;
  const spend =
    data.kind === 'PULSE'
      ? data.covered
        ? t('board.complete.fromPulses', { n: balance })
        : t('board.complete.pulsesShort', { n: balance })
      : balance > 0
        ? t('board.complete.fromPackage', { n: balance })
        : t('board.complete.payAtDesk');

  const submit = () => {
    onSubmit({
      ...(withAreas ? { areas: treated.map((id) => ({ serviceAreaId: id, pulses: fired(id) })) } : {}),
      ...(recordsPulses && !withAreas ? { pulsesDelivered: Number(total) } : {}),
      ...(machineId ? { machineId } : {}),
    });
  };

  return (
    <>
      <div className="modal__body">
        <p>{heading}</p>

        {recordsPulses ? (
          <>
            <p className="field__hint">{t('board.complete.pulsesHint')}</p>
            {withAreas ? (
              <ul style={{ listStyle: 'none', paddingInlineStart: 0 }}>
                {areas.map((area) => {
                  const id = area.serviceAreaId ?? '';
                  const name = localised.name(area);
                  const ticked = treated.includes(id);
                  return (
                    <li key={id} className="toolbar" style={{ alignItems: 'center' }}>
                      <label className="field__label">
                        <input
                          type="checkbox"
                          checked={ticked}
                          onChange={(event) =>
                            setTreated(
                              event.target.checked ? [...treated, id] : treated.filter((other) => other !== id),
                            )
                          }
                        />{' '}
                        {name}
                      </label>
                      <input
                        type="number"
                        className="field__input numeric"
                        aria-label={t('board.complete.pulsesFor', { area: name })}
                        min={1}
                        disabled={!ticked}
                        placeholder={t('board.complete.reference', { n: area.pulsePreset ?? 0 })}
                        value={pulses[id] ?? ''}
                        onChange={(event) => setPulses({ ...pulses, [id]: event.target.value })}
                        style={{ inlineSize: '9rem' }}
                      />
                    </li>
                  );
                })}
              </ul>
            ) : (
              <label className="field">
                <span className="field__label">{t('board.complete.total')}</span>
                <input
                  type="number"
                  className="field__input numeric"
                  min={1}
                  value={total}
                  onChange={(event) => setTotal(event.target.value)}
                />
              </label>
            )}
          </>
        ) : null}

        {machines.length > 0 ? (
          <label className="field">
            <span className="field__label">{t('board.complete.machine')}</span>
            <select className="field__input" value={machineId} onChange={(event) => setMachineId(event.target.value)}>
              <option value="">{t('board.complete.noMachine')}</option>
              {machines.map((machine) => (
                <option key={machine.machineId} value={machine.machineId}>
                  {machine.name ?? machine.code}
                  {machine.available ? '' : ` (${t('board.complete.unavailable')})`}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <p className="field__hint">{spend}</p>
        {error ? <ErrorState error={error} /> : null}
      </div>
      <footer className="modal__footer">
        <button type="button" className="button button--ghost" onClick={onClose}>
          {t('common.back')}
        </button>
        <button type="button" className="button button--primary" disabled={pending || !valid} onClick={submit}>
          {t('board.complete.confirm')}
        </button>
      </footer>
    </>
  );
}
